import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Share2, Facebook, MessageCircle, Instagram, Copy, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  buildPartnerShareMessage,
  buildPropertyShareMessage,
  type ShareResortContext,
} from '@/lib/share-text';

interface SocialShareProps {
  url: string;
  title: string;
  description?: string;
  className?: string;
  type?: 'property' | 'partner';
  /** Resort/empreendimento do imovel (nome, cidade, descricao). */
  resort?: ShareResortContext;
}

const SocialShare = ({
  url,
  title,
  description,
  className,
  type = 'property',
  resort,
}: SocialShareProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const fullUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${url}`
    : url;

  const getShareMessage = () => {
    if (type === 'property') {
      return buildPropertyShareMessage({
        title,
        propertyDescription: description,
        resort,
        fullUrl,
      });
    }
    return buildPartnerShareMessage({
      title,
      description,
      fullUrl,
    });
  };

  const handleWhatsAppShare = () => {
    const message = getShareMessage();
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const handleFacebookShare = () => {
    const encodedUrl = encodeURIComponent(fullUrl);
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

    const popup = window.open(
      shareUrl,
      'facebook-share-dialog',
      'width=626,height=436,menubar=no,toolbar=no,resizable=yes,scrollbars=yes'
    );

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      window.open(shareUrl, '_blank');
    }
  };

  const handleInstagramShare = async () => {
    const message = getShareMessage();
    try {
      await navigator.clipboard.writeText(message);
      toast({
        title: "Mensagem copiada!",
        description: "Cole a mensagem no Instagram para compartilhar.",
      });
    } catch (error) {
      console.error('Erro ao copiar mensagem:', error);
      toast({
        title: "Erro",
        description: "Não foi possível copiar a mensagem.",
        variant: "destructive",
      });
    }
  };

  const handleCopyLink = async () => {
    try {
      const message = getShareMessage();
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast({
        title: "Mensagem copiada!",
        description: "A mensagem com o link foi copiada para a área de transferência.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      toast({
        title: "Erro",
        description: "Não foi possível copiar a mensagem.",
        variant: "destructive",
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          <Share2 className="h-4 w-4 mr-2" />
          Compartilhar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={handleWhatsAppShare}>
          <MessageCircle className="h-4 w-4 mr-2 text-green-600" />
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleFacebookShare}>
          <Facebook className="h-4 w-4 mr-2 text-blue-600" />
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleInstagramShare}>
          <Instagram className="h-4 w-4 mr-2 text-pink-600" />
          Instagram
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopyLink}>
          {copied ? (
            <>
              <Check className="h-4 w-4 mr-2 text-green-600" />
              Copiado!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-2" />
              Copiar Link
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SocialShare;
